import type { ReactNode } from 'react';

type VisitingCardPhoneFrameProps = {
  children: ReactNode;
  /**
   * Hard-lock to the viewport (no document scroll). Used by `/a` news editor so the
   * phone shell never grows with content — scrolling stays inside children.
   */
  viewportLock?: boolean;
};

/**
 * Phones in both orientations: full viewport. Roomy screens: smartphone bezel.
 *
 * The `bezel` variant (see index.css) asks for width *and* height: a phone in landscape clears
 * the `sm` breakpoint but is only ~400px tall, which used to squeeze the card — and the
 * full-screen photo viewer inside it — into a tiny square.
 */
export function VisitingCardPhoneFrame({
  children,
  viewportLock = false,
}: VisitingCardPhoneFrameProps) {
  return (
    <div
      className={[
        'flex items-center justify-center bg-stone-950 bezel:px-6 bezel:py-8',
        viewportLock
          ? 'h-dvh max-h-dvh w-full max-w-[100dvw] overflow-hidden overscroll-none'
          : 'min-h-dvh',
      ].join(' ')}
    >
      <div
        className={[
          'relative h-[100dvh] w-full max-w-[100dvw] overflow-hidden overscroll-none bg-stone-100 text-stone-900',
          'bezel:h-[min(844px,calc(100dvh-4rem))] bezel:w-[min(390px,100%)] bezel:max-w-[430px]',
          'bezel:rounded-[2.4rem] bezel:border-[10px] bezel:border-stone-800',
          'bezel:shadow-[0_25px_80px_rgba(0,0,0,0.45)]',
        ].join(' ')}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 hidden h-7 items-end justify-center bezel:flex">
          <div className="mb-1 h-4 w-28 rounded-full bg-stone-900/90" />
        </div>
        <div className="h-full max-h-full w-full max-w-full overflow-hidden overscroll-none pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] bezel:pt-7">
          {children}
        </div>
      </div>
    </div>
  );
}
